import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES } from "@helpers/constantes";
import {
  BofuTabDocument,
  BofuTabStatus,
  DocumentResponse,
  TabsConfigResponseDocument,
  TabsConfigResponseForm,
  PersonaStructure,
  BofuDocumentRelationship,
  GarantiasVehicularInmobiliariaResponse,
  FORM_STATUS_ENUM,
  BofuTabObject,
  BofuTabFormsAval,
  BofuTabFormsEmpresa,
  BofuTabFormsRepresentante,
} from "@interfaces/response";
import { PersonalData } from "@interfaces/store";
import { UUID } from "crypto";

export const validateDocumentStructure = (document: TabsConfigResponseDocument) => {
  if (
    !document.name ||
    !document.label ||
    !document.file_category_id ||
    !document.file_category_code ||
    !document.relationship
  ) {
    return false;
  }

  // documents with this flag are only available for bpm frontend
  if (document.requires_bpm_only) {
    return false;
  }

  return true;
};

export const filterDocumentsByGuarantee = (
  document: TabsConfigResponseDocument,
  guarantee_data: GarantiasVehicularInmobiliariaResponse[],
) => {
  const guarantee_codes = guarantee_data.map((item) => item?.guarantee_type?.code);
  const property_type_codes = guarantee_data.map((item) => item?.property_type?.code);

  if (document.guarantee_type_code) {
    if (guarantee_codes.includes(document.guarantee_type_code)) {
      return true;
    }
    return false;
  } else if (document.property_type_code) {
    if (property_type_codes.includes(document.property_type_code)) {
      return true;
    }
    return false;
  }

  return true;
};

export const someDocumentRejected = (documents: BofuTabDocument[]): boolean => {
  // identificar los documentos unicos/multiples
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  // validar si algun documento esta rechazado
  const result_unique = unique_documents.some((item) => item.file_value?.validated === false);
  if (result_unique) {
    return true;
  }

  const result_multiple = multiple_documents.some((item) => {
    return item?.list_values?.some((item) => item?.validated === false);
  });
  if (result_multiple) {
    return true;
  }

  return false;
};

export const allDocumentValidated = (documents: BofuTabDocument[]): boolean => {
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  const result_unique = unique_documents.every((item) => item.file_value?.validated === true);
  const result_multiple = multiple_documents.every((item) =>
    item?.list_values?.every((item) => item?.validated === true),
  );

  return result_unique && result_multiple;
};

export const allDocumentUploaded = (documents: BofuTabDocument[]): boolean => {
  const unique_documents = documents.filter((item) => !item.multiple);
  const multiple_documents = documents.filter((item) => item.multiple);

  const result_unique = unique_documents.every((item) => item.file_value);
  const result_multiple = multiple_documents.every((item) => {
    // si se requiere al menos un documento seria con lo siguiente:
    // return item?.list_values?.some((item) => item);
    return item?.list_values?.every((item) => item);
  });

  return result_unique && result_multiple;
};

interface generateTabLabelProps {
  tab_documents: BofuTabDocument[];
  other_documents?: (DocumentResponse | undefined)[];
  forms?: FORM_STATUS_ENUM[];
}

export const generateTabLabel = ({
  tab_documents,
  other_documents,
  forms,
}: generateTabLabelProps): BofuTabStatus | undefined => {
  // filtrar los documentos requeridos
  const required_documents = tab_documents.filter((item) => item.required);

  if (required_documents.length === 0 && other_documents?.length === 0 && forms?.length === 0) {
    return undefined;
  }

  // validar si algun documento esta rechazado
  const some_document_rejected = someDocumentRejected(required_documents);
  const some_other_document_rejected = other_documents?.length
    ? other_documents?.some((item) => item?.validated === false)
    : false;
  const some_form_rejected = forms?.length ? forms?.some((item: FORM_STATUS_ENUM) => item === "INCIDENCIA") : false;
  if (some_document_rejected || some_other_document_rejected || some_form_rejected) {
    return "incomplete";
  }

  // validar si todos los documentos estan validados
  const all_documents_validated = allDocumentValidated(required_documents);
  const all_other_documents_validated = other_documents?.length
    ? other_documents?.every((item) => item?.validated === true)
    : true;
  const all_forms_validated = forms?.length ? forms?.every((item: FORM_STATUS_ENUM) => item === "VALIDADO") : true;
  if (all_documents_validated && all_other_documents_validated && all_forms_validated) {
    return "validado";
  }

  // validar si todos los documentos estan cargados
  const all_documents_uploaded = allDocumentUploaded(required_documents);
  const all_other_documents_uploaded = other_documents?.length ? other_documents?.every((item) => item) : true;
  const all_forms_completed = forms?.length
    ? forms?.every((item: FORM_STATUS_ENUM) => item === "COMPLETO" || item === "VALIDADO")
    : true;
  if (all_documents_uploaded && all_other_documents_uploaded && all_forms_completed) {
    return "cargado";
  }

  return undefined;
};

export const validateRequiredDocumentsAreUploaded = (documents: BofuTabDocument[]) => {
  const required_documents = documents.filter((item) => item.required);

  return required_documents.every((item) => {
    if (item.multiple) {
      return item.list_values?.some((value) => value !== undefined);
    }
    return item.file_value !== undefined;
  });
};

interface IdentityDocumentsProps {
  documents: DocumentResponse[];
  representante?: PersonaStructure;
  aval?: PersonaStructure;
  client_details?: PersonalData;
  propietarios?: PersonaStructure[];
  representantes?: PersonaStructure[];
  avales_adicionales?: PersonaStructure[];
  conyuges?: PersonaStructure[];
}

export const identityDocumentsByPerson = ({
  documents,
  representante,
  aval,
  client_details,
  propietarios,
  representantes,
  avales_adicionales,
  conyuges,
}: IdentityDocumentsProps) => {
  const propietarios_ids = propietarios?.map((item) => item.id);
  const representantes_ids = representantes?.map((item) => item.id);
  const avales_adicionales_ids = avales_adicionales?.map((item) => item.id);
  const conyuges_ids = conyuges?.map((item) => item.id);

  const docs_account = client_details ? documents.filter((item) => item.client_details_id === client_details.id) : [];
  const docs_representante = representante ? documents.filter((item) => item.person_id === representante.id) : [];
  const docs_aval = aval ? documents.filter((item) => item.person_id === aval.id) : [];
  const docs_guarantee = documents.filter((item) => item.guarantee_id);
  const docs_propietarios = propietarios
    ? documents.filter((item) => propietarios_ids?.includes(item.person_id as UUID))
    : [];
  const docs_representantes = representantes
    ? documents.filter((item) => representantes_ids?.includes(item.person_id as UUID))
    : [];
  const docs_avales_adicionales = avales_adicionales
    ? documents.filter((item) => avales_adicionales_ids?.includes(item.person_id as UUID))
    : [];
  const docs_conyuges_avales = conyuges
    ? documents.filter((item) => conyuges_ids?.includes(item.person_id as UUID))
    : [];

  return {
    docs_account,
    docs_representante,
    docs_aval,
    docs_guarantee,
    docs_propietarios,
    docs_representantes,
    docs_avales_adicionales,
    docs_conyuges_avales,
  };
};

interface GenerateDocumentConfigProps {
  docItem: TabsConfigResponseDocument;
  docs_account?: DocumentResponse[];
  docs_aval: DocumentResponse[];
  clientDetailsId: UUID;
  aval: PersonaStructure | undefined;
}

export const generateDocumentConfig = ({
  docItem,
  docs_account,
  docs_aval,
  clientDetailsId,
  aval,
}: GenerateDocumentConfigProps): BofuTabDocument => {
  let document_value: DocumentResponse | undefined;
  let list_values: Array<DocumentResponse | undefined> = [];
  let client_details_id: string | undefined;
  let person_id: string | undefined;
  let disabled = true;
  let upload_type: "client" | "person" | undefined;
  let disabled_reason: string | undefined;

  switch (docItem.relationship) {
    case "client_details":
      document_value = docs_account?.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_account?.filter((item) => item.file_code === docItem.file_category_code) || [];
      client_details_id = clientDetailsId;
      person_id = undefined;
      disabled = false;
      upload_type = "client";
      break;
    case "representante_legal":
      document_value = undefined;
      list_values = [];
      client_details_id = undefined;
      person_id = undefined;
      disabled = true;
      disabled_reason = "Representante legal no encontrado";
      upload_type = undefined;
      break;
    case "aval":
      document_value = docs_aval.find((item) => item.file_code === docItem.file_category_code);
      list_values = docs_aval.filter((item) => item.file_code === docItem.file_category_code);
      client_details_id = clientDetailsId;
      person_id = aval ? aval.id : undefined;
      disabled = aval ? false : true;
      disabled_reason = aval ? undefined : "Aval no encontrado";
      upload_type = "person";
      break;
    case "conyuge_aval":
      // estos pueden ser varios
      document_value = undefined;
      list_values = [];
      client_details_id = undefined;
      person_id = undefined;
      disabled = true;
      disabled_reason = "Conyuge de aval no encontrado";
      upload_type = undefined;
      break;
    case "propietario_real":
      // Estos se retornan vacios por que puede haber varios propietarios reales
      document_value = undefined;
      list_values = [];
      client_details_id = undefined;
      person_id = undefined;
      disabled = true;
      disabled_reason = "Propietario real no encontrado";
      upload_type = undefined;
      break;
    case "segundo_aval":
      // estos pueden ser varios
      document_value = undefined;
      list_values = [];
      client_details_id = undefined;
      person_id = undefined;
      disabled = true;
      disabled_reason = "Segundo aval no encontrado";
      upload_type = undefined;
      break;
  }

  return {
    name: docItem.name,
    label: docItem.label,
    order: docItem.order,
    required: docItem.required,
    file_category_id: docItem.file_category_id,
    file_category_code: docItem.file_category_code,
    relationship: docItem.relationship as BofuDocumentRelationship,
    file_value: document_value,
    client_details_id: client_details_id as UUID,
    person_id: person_id as UUID,
    guarantee_id: undefined,
    disabled,
    disabled_reason,
    upload_type,
    multiple: docItem.multiple || false,
    list_values: list_values.length ? list_values : [undefined],
    validation_type: docItem.validation_type,
    specific_validations: docItem.specific_validations
      ? docItem.specific_validations.split(",").map((item) => item.trim())
      : undefined,
  };
};

interface GeneratePropietarioDocsProps {
  personItem: PersonaStructure;
  valid_documents: BofuTabDocument[];
  documents_uploaded: DocumentResponse[];
  clientDetailsId: UUID;
}

/**
 * Esta funcion se encarga de generar el array de documentos estandar para una persona
 * @param {PersonaStructure} propietarioItem - El propietario real
 * @param {BofuTabDocument[]} valid_documents - Los documentos validados
 * @param {DocumentResponse[]} documents_uploaded - Los documentos cargados
 * @param {UUID} clientDetailsId - El ID del cliente
 * @returns {BofuTabDocument[]} - Los documentos generados
 */
export const generatePropietarioDocs = ({
  personItem,
  valid_documents,
  documents_uploaded,
  clientDetailsId,
}: GeneratePropietarioDocsProps): BofuTabDocument[] => {
  const copy_documents = JSON.parse(JSON.stringify(valid_documents)) as BofuTabDocument[];

  copy_documents.forEach((documentItem) => {
    documentItem.client_details_id = clientDetailsId;
    documentItem.person_id = personItem.id as UUID;
    documentItem.disabled = false;
    documentItem.upload_type = "person";
    documentItem.list_values = [];
    documentItem.file_value = undefined;

    if (documentItem.multiple) {
      documentItem.list_values = documents_uploaded
        .filter((item) => item.file_code === documentItem.file_category_code)
        .filter((item) => item.person_id === personItem.id);

      if (documentItem.list_values.length === 0) {
        documentItem.list_values.push(undefined);
      }
    } else {
      documentItem.file_value = documents_uploaded.find(
        (item) => item.file_code === documentItem.file_category_code && item.person_id === personItem.id,
      );
    }
  });

  return copy_documents;
};

interface generateGuaranteeDocsProps {
  guaranteeItem: GarantiasVehicularInmobiliariaResponse;
  valid_documents: BofuTabDocument[];
  documents_uploaded: DocumentResponse[];
  clientDetailsId: UUID;
}

export const generateGuaranteeDocs = ({
  clientDetailsId,
  guaranteeItem,
  valid_documents,
  documents_uploaded,
}: generateGuaranteeDocsProps): BofuTabDocument[] => {
  const copy_documents = JSON.parse(JSON.stringify(valid_documents)) as BofuTabDocument[];

  copy_documents.forEach((documentItem) => {
    documentItem.client_details_id = clientDetailsId;
    documentItem.person_id = undefined;
    documentItem.guarantee_id = guaranteeItem.id;
    documentItem.upload_type = "client";
    documentItem.list_values = [];
    documentItem.file_value = undefined;

    if (documentItem.multiple) {
      documentItem.list_values = documents_uploaded
        .filter((item) => item.file_code === documentItem.file_category_code)
        .filter((item) => item.guarantee_id === guaranteeItem.id);

      if (documentItem.list_values.length === 0) {
        documentItem.list_values.push(undefined);
      }
    } else {
      documentItem.file_value = documents_uploaded.find(
        (item) => item.file_code === documentItem.file_category_code && item.guarantee_id === guaranteeItem.id,
      );
    }
  });

  return copy_documents;
};

interface GenerateGuaranteeTitleProps {
  guarantee_item: GarantiasVehicularInmobiliariaResponse;
  guarantee_index: number;
}

// todo: deprecate this function and use the new component BofuGuaranteeTitle
export const generateGuaranteeTitle = ({ guarantee_item, guarantee_index }: GenerateGuaranteeTitleProps) => {
  if (guarantee_item.details) {
    const { make, model, year, version } = guarantee_item.details;
    if (make && model && year && version) {
      return `${make} | ${model} | ${year} | ${version}`;
    }
  }
  return `Garantía ${guarantee_index + 1}`;
};

export const isFormAvailable = (form_status: FORM_STATUS_ENUM) => {
  if (form_status === "COMPLETO" || form_status === "VALIDADO") {
    return false;
  }
  return true;
};

export const mapBofuTabEmpresaForms = (forms?: Array<TabsConfigResponseForm>): BofuTabFormsEmpresa => {
  if (!forms?.length) {
    return {
      solicitud_credito: {
        enabled: true,
        config: {},
      },
      referencias_personales: true,
      informacion_bancaria: true,
    };
  }

  const solicitud_credito = forms.find((form) => form.code === BOFU_TABS_FORM_CODES.solicitud_credito);

  return {
    solicitud_credito: {
      enabled: Boolean(solicitud_credito),
      config: solicitud_credito?.config ?? {},
    },
    referencias_personales: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.referencias_personales),
    informacion_bancaria: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.informacion_bancaria),
  };
};

export const mapBofuTabAvalForms = (forms?: Array<TabsConfigResponseForm>): BofuTabFormsAval => {
  if (!forms?.length) {
    return {
      informacion_aval: true,
      validacion_identidad: true,
      informacion_bancaria: true,
    };
  }

  return {
    informacion_aval: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.informacion_aval),
    validacion_identidad: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.validacion_identidad),
    informacion_bancaria: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.informacion_bancaria),
  };
};

export const mapBofuTabRepresentanteForms = (forms?: Array<TabsConfigResponseForm>): BofuTabFormsRepresentante => {
  if (!forms?.length) {
    return {
      informacion_representante_legal: true,
      validacion_identidad: true,
      informacion_bancaria: true,
    };
  }

  return {
    informacion_representante_legal: forms.some(
      (form) => form.code === BOFU_TABS_FORM_CODES.informacion_representante_legal,
    ),
    validacion_identidad: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.validacion_identidad),
    informacion_bancaria: forms.some((form) => form.code === BOFU_TABS_FORM_CODES.informacion_bancaria),
  };
};

export const initializeBofuTabsObject = (): BofuTabObject => {
  return {
    dictamen: {
      code: BOFU_TABS_CONFIG.tab_dictamen.code,
      url: BOFU_TABS_CONFIG.tab_dictamen.url,
      order: 1,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
    },
    empresa: {
      code: BOFU_TABS_CONFIG.tab_empresa.code,
      url: BOFU_TABS_CONFIG.tab_empresa.url,
      order: 2,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        solicitud_credito: {
          enabled: true,
          config: {},
        },
        referencias_personales: true,
        informacion_bancaria: true,
      },
      doc_solicitud_credito: undefined,
    },
    aval: {
      code: BOFU_TABS_CONFIG.tab_aval.code,
      url: BOFU_TABS_CONFIG.tab_aval.url,
      order: 3,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        informacion_aval: true,
        validacion_identidad: true,
        informacion_bancaria: true,
      },
      documents_conyuge: [],
      doc_anexo_1: undefined,
      doc_validacion_identidad: undefined,
      show_form_aval: true,
      doc_anexo_1_conyuge: undefined,
      doc_validacion_identidad_conyuge: undefined,
      show_information_form_conyuge: false,
    },
    propietarios_reales: {
      code: BOFU_TABS_CONFIG.tab_propietario.code,
      url: BOFU_TABS_CONFIG.tab_propietario.url,
      order: 4,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      propietarios: [],
    },
    garantia: {
      code: BOFU_TABS_CONFIG.tab_garantia.code,
      url: BOFU_TABS_CONFIG.tab_garantia.url,
      order: 5,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      items: [],
    },
    representantes: {
      code: BOFU_TABS_CONFIG.tab_representante.code,
      url: BOFU_TABS_CONFIG.tab_representante.url,
      order: 6,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        informacion_representante_legal: true,
        validacion_identidad: true,
        informacion_bancaria: true,
      },
      items: [],
    },
  };
};

interface FindDocumentValueProps {
  documents: DocumentResponse[];
  file_code: string;
  person_id: string;
}

export const findDocumentValue = ({ documents, file_code, person_id }: FindDocumentValueProps) => {
  return documents.find((item) => item.file_code === file_code && item.person_id === person_id);
};
